import React, { useEffect, useMemo, useState } from 'react';
import { Layers, Lightbulb, SlidersHorizontal, PieChart, Clock, RefreshCw } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { themeAlpine } from 'ag-grid-community';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import {
  getSourceSystem,
  getTaskArray,
  getTaskAttributesRaw,
  getVariantArrayUnfiltered,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { openFindingForContext } from '@process-advisor/process-miner/shared/findingContext';
import { getSegmentAttributePriorityForSource } from '@process-advisor/process-miner/sources/fieldProfile';
import { SOURCE_SYSTEMS, normalizeSourceSystem } from '@process-advisor/process-miner/sources/capabilities';
import EvidenceHeader, { evidenceHeaderControlLabelStyle, evidenceHeaderSelectStyle } from '@process-advisor/process-miner/dashboard/shared/EvidenceHeader';
import StatCard from '@process-advisor/process-miner/dashboard/shared/StatCard';
import InsightStrip from '@process-advisor/process-miner/dashboard/shared/InsightStrip';
import EvidenceFooterNote from '@process-advisor/process-miner/dashboard/shared/EvidenceFooterNote';
import { badgeCellRenderer, tierForRate } from '@process-advisor/process-miner/dashboard/shared/metricBadge';
import '@process-advisor/process-miner/shared/gridStyles.css';
import '@process-advisor/process-miner/dashboard/shared/evidenceGridStyles.css';

const isFreeEdition = () => typeof window !== 'undefined' && window.__METRICUS_JIRA_PRODUCT_EDITION__ === 'free';

const C = {
  text: '#172B4D',
  body: '#42526E',
  muted: '#6B778C',
  border: '#DFE1E6',
  bg: '#F4F5F7',
  card: '#FFFFFF',
  primary: '#0052CC',
  accent: '#00A3BF',
  primaryTint: '#F7F9FC',
  green: '#00875A',
  orange: '#FF8B00',
  red: '#DE350B',
};

const METRIC_HELP = {
  segment: 'The selected attribute value used to group work items, such as Priority, Assignment Group, Close Code, Issue Type, or ServiceNow context field.',
  tasks: 'Number of work items in this segment after current process filters are applied.',
  share: 'The percentage of all visible work items represented by this segment. High-share segments usually deserve attention first.',
  avgDuration: 'Average lead/cycle time for work items in this segment, calculated from each task duration in the processed event log.',
  avgTransitions: 'Average number of activity changes per work item in this segment. Higher values can indicate routing complexity, handoffs, or rework.',
  reworkRate: 'Percentage of work items in this segment with a rework signal. The current signal is based on looped variants or unusually high transition counts.',
  avgWaitTime: 'Average waiting/delay time for work items in this segment when wait-time data exists. Blank means the model does not currently have wait-time data for this metric.',
  avgFlowEfficiency: 'Average flow efficiency for the segment when available. Lower values suggest more elapsed time is waiting/delay rather than active work.',
  finding: 'Save this segment as an evidence-backed finding for the Assessment.',
};

function metricTooltip(metricKey) {
  return METRIC_HELP[metricKey] || '';
}


function normKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

const SERVICE_NOW_ATTRIBUTE_LABELS = {
  priority: 'Priority',
  assignment_group: 'Assignment Group',
  assigned_to: 'Assigned To',
  opened_by: 'Opened By',
  closed_by: 'Closed By',
  category: 'Category',
  subcategory: 'Subcategory',
  business_service: 'Business Service',
  service_offering: 'Service Offering',
  cmdb_ci: 'Configuration Item',
  company: 'Company',
  location: 'Location',
  contact_type: 'Contact Type',
  impact: 'Impact',
  urgency: 'Urgency',
  severity: 'Severity',
};

function titleCaseAttribute(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === 'null' || raw === 'undefined') return 'Attribute';
  const normalized = normKey(raw);
  if (SERVICE_NOW_ATTRIBUTE_LABELS[normalized]) return SERVICE_NOW_ATTRIBUTE_LABELS[normalized];

  return raw
    .replace(/[_\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => {
      const lower = word.toLowerCase();
      if (lower === 'sla') return 'SLA';
      if (lower === 'ci') return 'CI';
      if (lower === 'id') return 'ID';
      return lower ? lower.charAt(0).toUpperCase() + lower.slice(1) : '';
    })
    .join(' ');
}


function displayAttr(attr) {
  const field = attr?.field || attr?.attributeID || attr?.id || '';
  const label = attr?.display || attr?.label || field;
  const normalizedField = normKey(field);
  const normalizedLabel = normKey(label);
  if (SERVICE_NOW_ATTRIBUTE_LABELS[normalizedField]) return SERVICE_NOW_ATTRIBUTE_LABELS[normalizedField];
  if (SERVICE_NOW_ATTRIBUTE_LABELS[normalizedLabel]) return SERVICE_NOW_ATTRIBUTE_LABELS[normalizedLabel];
  return titleCaseAttribute(label || field);
}


function isSegmentableAttribute(attr) {
  const key = normKey(attr?.attributeID || attr?.field || attr?.id || attr?.label);
  const label = normKey(attr?.label || attr?.display || attr?.field || attr?.id);

  // Segments should group by model attributes chosen for analysis, not derived
  // task_sla metrics. SLA metrics have their own Evidence tab and are measures,
  // not useful grouping dimensions here.
  if (!key && !label) return false;
  if (key.startsWith('sla_') || label.startsWith('sla_')) return false;
  if (/^sla($|_)/.test(key) || /^sla($|_)/.test(label)) return false;
  if (/(sla_records|breached_count|has_breach|active_count|paused_count|completed_count|cancelled_count|business_elapsed|business_time_left|pause_hours|max_business_percentage|sla_names|sla_stages)/i.test(key)) return false;
  if (/(sla records|sla breached|active sla|paused sla|completed sla|cancelled sla|sla business|sla pause|sla max|sla names|sla stages)/i.test(String(attr?.label || attr?.display || ''))) return false;
  return true;
}

function pickDefaultAttribute(attrs, sourceSystem) {
  if (!attrs.length) return '';
  const byKey = new Map();
  attrs.forEach(a => {
    [a.attributeID, a.field, a.label, a.id].forEach(v => {
      const k = normKey(v);
      if (k && !byKey.has(k)) byKey.set(k, a.id);
    });
  });
  const priority = getSegmentAttributePriorityForSource(sourceSystem);
  for (const wanted of priority) {
    const key = normKey(wanted);
    if (byKey.has(key)) return byKey.get(key);
  }
  return attrs[0].id;
}

function cleanValue(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === 'null' || raw === 'undefined') return '(blank)';
  return raw.replaceAll('^^', ', ');
}

function formatNumber(n) {
  return Math.round(Number(n || 0)).toLocaleString();
}

function formatDuration(seconds) {
  const s = Number(seconds || 0);
  if (!Number.isFinite(s) || s <= 0) return '0m';
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function pct(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0%';
  return `${Math.round(n * 100)}%`;
}

function taskValue(task, attr) {
  if (!task || !attr) return '';
  const keys = [attr.id, attr.field, attr.attributeID, attr.label].filter(Boolean);
  for (const key of keys) {
    if (task[key] !== undefined && task[key] !== null && String(task[key]).trim() !== '') return task[key];
  }
  return '';
}

function taskTransitions(task) {
  const explicit = Number(task?.transitions);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const si = Number(task?.si);
  const fi = Number(task?.fi);
  if (Number.isFinite(si) && Number.isFinite(fi) && fi >= si) return Math.max(0, fi - si + 1);
  return 0;
}

function buildRows({ tasks, variants, attr, minTasks }) {
  const groups = new Map();
  const visible = (tasks || []).filter(t => t && (t.filtered === 0 || t.filtered === undefined));
  visible.forEach(task => {
    const value = cleanValue(taskValue(task, attr));
    if (!groups.has(value)) {
      groups.set(value, {
        segment: value,
        tasks: 0,
        duration: 0,
        transitions: 0,
        reworkTasks: 0,
        waitTime: 0,
        touchTime: 0,
        queueTime: 0,
        adminTime: 0,
        flowEfficiency: 0,
        flowCount: 0,
      });
    }
    const row = groups.get(value);
    const duration = Number(task.d || task.Duration || 0) || 0;
    const transitions = taskTransitions(task);
    const v = variants?.[task.vid];
    const hasRework = (Number(v?.loops || 0) > 0) || transitions > 5;
    row.tasks += 1;
    row.duration += duration;
    row.transitions += transitions;
    if (hasRework) row.reworkTasks += 1;
    row.waitTime += Number(task.wait_time_seconds || 0) || 0;
    row.touchTime += Number(task.touch_time_seconds || 0) || 0;
    row.queueTime += Number(task.queue_time_seconds || 0) || 0;
    row.adminTime += Number(task.admin_time_seconds || 0) || 0;
    if (Number.isFinite(Number(task.flow_efficiency))) {
      row.flowEfficiency += Number(task.flow_efficiency || 0);
      row.flowCount += 1;
    }
  });

  const totalTasks = visible.length || 1;
  const rows = [...groups.values()]
    .filter(r => r.tasks >= minTasks)
    .map(r => ({
      ...r,
      share: r.tasks / totalTasks,
      avgDuration: r.tasks ? r.duration / r.tasks : 0,
      avgTransitions: r.tasks ? r.transitions / r.tasks : 0,
      reworkRate: r.tasks ? r.reworkTasks / r.tasks : 0,
      avgWaitTime: r.tasks ? r.waitTime / r.tasks : 0,
      avgTouchTime: r.tasks ? r.touchTime / r.tasks : 0,
      avgQueueTime: r.tasks ? r.queueTime / r.tasks : 0,
      avgAdminTime: r.tasks ? r.adminTime / r.tasks : 0,
      avgFlowEfficiency: r.flowCount ? r.flowEfficiency / r.flowCount : null,
    }));

  rows.sort((a, b) => b.tasks - a.tasks || b.avgDuration - a.avgDuration);
  return { rows, totalTasks: visible.length, hiddenSegments: Math.max(0, groups.size - rows.length), totalSegments: groups.size };
}


export default function SegmentInsights({ refreshKey, processLabel = 'Process', entityLabel = 'Work Item' }) {
  const [versionTick, setVersionTick] = useState(0);
  const [selectedAttrId, setSelectedAttrId] = useState('');


  useEffect(() => {
    setVersionTick(v => v + 1);
  }, [refreshKey]);

  const sourceSystem = normalizeSourceSystem(getSourceSystem ? getSourceSystem() : 'generic');
  const isServiceNow = sourceSystem === SOURCE_SYSTEMS.SERVICENOW;

  const attrs = useMemo(() => {
    const raw = (getTaskAttributesRaw ? getTaskAttributesRaw() : []) || [];
    return raw
      .filter(a => a && a.id)
      .map(a => ({ ...a, display: displayAttr(a), key: normKey(a.attributeID || a.field || a.label || a.id) }))
      .filter(isSegmentableAttribute);
  }, [versionTick]);

  useEffect(() => {
    if (!selectedAttrId && attrs.length) setSelectedAttrId(pickDefaultAttribute(attrs, sourceSystem));
  }, [attrs, selectedAttrId, sourceSystem]);

  const selectedAttr = attrs.find(a => a.id === selectedAttrId) || attrs[0] || null;

  const segmentData = useMemo(() => {
    if (!selectedAttr) return { rows: [], totalTasks: 0, hiddenSegments: 0, totalSegments: 0 };
    const result = buildRows({
      tasks: getTaskArray ? getTaskArray() : [],
      variants: getVariantArrayUnfiltered ? getVariantArrayUnfiltered() : [],
      attr: selectedAttr,
      minTasks: 1,
    });
    return result;
  }, [selectedAttr, versionTick]);

  const totalDuration = segmentData.rows.reduce((sum, r) => sum + r.duration, 0);
  const overallAvg = segmentData.totalTasks ? totalDuration / Math.max(1, segmentData.rows.reduce((sum, r) => sum + r.tasks, 0)) : 0;
  const topSegment = segmentData.rows[0] || null;
  const slowestSegment = [...segmentData.rows].sort((a, b) => b.avgDuration - a.avgDuration)[0] || null;
  const highestRework = [...segmentData.rows].sort((a, b) => b.reworkRate - a.reworkRate)[0] || null;


  const styles = {
    page: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: C.text },
    gridWrap: { width: '100%' },
    saveBtn: { border: '1px solid #C1C7D0', background: '#FFFFFF', color: C.primary, borderRadius: 6, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    empty: { padding: 24, textAlign: 'center', color: C.muted, background: C.card, border: `1px dashed ${C.border}`, borderRadius: 10 },
  };

  const saveSegmentFinding = (row) => {
    const label = selectedAttr ? displayAttr(selectedAttr) : 'Attribute';
    openFindingForContext(apiProvider, {
      source: 'Evidence - Segments',
      objectType: 'Segment',
      objectLabel: `${label}: ${row.segment}`,
      objectId: `${selectedAttr?.id || label}:${row.segment}`,
      category: 'Performance',
      evidence: `${row.segment} represents ${formatNumber(row.tasks)} ${entityLabel.toLowerCase()}s (${pct(row.share)}), with average lead time ${formatDuration(row.avgDuration)}, average transitions ${row.avgTransitions.toFixed(1)}, and rework signal ${pct(row.reworkRate)}.`,
      row: {
        attribute: label,
        segment: row.segment,
        tasks: row.tasks,
        share: row.share,
        avg_lead_time_seconds: row.avgDuration,
        avg_transitions: row.avgTransitions,
        rework_rate: row.reworkRate,
      },
      title: `${label}: ${row.segment} finding`,
    });
  };

  if (!attrs.length) {
    return (
      <div style={styles.page}>
        <div style={styles.empty}>
          <Layers size={28} />
          <div style={{ fontWeight: 700, marginTop: 8 }}>No context fields available</div>
          <div style={{ marginTop: 4, fontSize: 13 }}>Add analysis fields to the model to segment evidence by priority, ownership, classification, or other populated context fields.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <EvidenceHeader
        icon={<SlidersHorizontal size={18} />}
        title="Segment Evidence"
        subtitle={`Break down performance by ${isServiceNow ? 'ServiceNow context fields' : 'task attributes'} to find the ${entityLabel.toLowerCase()}s driving delay, rework, and routing complexity.`}
        actions={(
          <>
            <label style={evidenceHeaderControlLabelStyle}>Segment by</label>
            <select value={selectedAttrId} onChange={e => setSelectedAttrId(e.target.value)} style={{ ...evidenceHeaderSelectStyle, minWidth: 200 }}>
              {attrs.map(a => <option key={a.id} value={a.id}>{a.display}</option>)}
            </select>
          </>
        )}
      />

      <div className="evidence-metric-grid">
        <StatCard icon={Layers} label="Segments" value={formatNumber(segmentData.rows.length)} detail={selectedAttr ? `By ${displayAttr(selectedAttr)}` : null} color={C.primary} />
        <StatCard icon={PieChart} label="Largest Segment" value={topSegment ? pct(topSegment.share) : '—'} detail={topSegment ? `${topSegment.segment} · ${formatNumber(topSegment.tasks)} ${entityLabel.toLowerCase()}s` : 'Not available'} color={C.accent} />
        <StatCard icon={Clock} label="Slowest Segment" value={slowestSegment ? formatDuration(slowestSegment.avgDuration) : '—'} detail={slowestSegment ? slowestSegment.segment : 'Not available'} color={overallAvg && slowestSegment?.avgDuration > overallAvg * 1.25 ? C.orange : C.primary} />
        <StatCard icon={RefreshCw} label="Highest Rework" value={highestRework ? pct(highestRework.reworkRate) : '—'} detail={highestRework ? highestRework.segment : 'Not available'} color={highestRework && highestRework.reworkRate >= 0.25 ? C.red : C.green} />
      </div>

      {segmentData.rows.length === 0 ? (
        <div style={styles.empty}>No populated segments found for this field.</div>
      ) : (
        <div style={styles.gridWrap} className="segment-evidence-grid evidence-grid-shell evidence-ag-grid">
          <AgGridReact
            theme={themeAlpine}
            rowData={segmentData.rows}
            columnDefs={[
              {
                headerName: 'Segment',
                field: 'segment',
                minWidth: 210,
                flex: 1.4,
                pinned: 'left',
                filter: 'agTextColumnFilter',
                cellStyle: { fontWeight: 700, color: C.text },
                tooltipField: 'segment',
                headerTooltip: metricTooltip('segment'),
                tooltipValueGetter: p => `${p.value || ''}

${metricTooltip('segment')}`,
                menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
              },
              {
                headerName: 'Tasks',
                field: 'tasks',
                type: 'numericColumn',
                headerClass: 'num-header',
                cellClass: 'num-cell',
                minWidth: 90,
                flex: 0.7,
                filter: 'agNumberColumnFilter',
                valueFormatter: p => formatNumber(p.value),
                headerTooltip: metricTooltip('tasks'),
                tooltipValueGetter: p => `${formatNumber(p.value)} ${entityLabel.toLowerCase()}s

${metricTooltip('tasks')}`,
                menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
              },
              {
                headerName: 'Share',
                field: 'share',
                type: 'numericColumn',
                headerClass: 'num-header',
                cellClass: 'num-cell',
                minWidth: 90,
                flex: 0.7,
                filter: 'agNumberColumnFilter',
                valueFormatter: p => pct(p.value),
                headerTooltip: metricTooltip('share'),
                tooltipValueGetter: p => `${pct(p.value)}

${metricTooltip('share')}`,
                menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
              },
              {
                headerName: 'Avg Lead Time',
                field: 'avgDuration',
                type: 'numericColumn',
                headerClass: 'num-header',
                cellClass: 'num-cell',
                minWidth: 120,
                flex: 0.85,
                filter: 'agNumberColumnFilter',
                valueFormatter: p => formatDuration(p.value),
                headerTooltip: metricTooltip('avgDuration'),
                tooltipValueGetter: p => `${formatDuration(p.value)}

${metricTooltip('avgDuration')}`,
                menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
                cellRenderer: badgeCellRenderer(
                  v => formatDuration(v),
                  v => {
                    if (!overallAvg) return 'neutral';
                    if (v > overallAvg * 1.5) return 'high';
                    if (v > overallAvg * 1.25) return 'watch';
                    return 'neutral';
                  }
                ),
              },
              {
                headerName: 'Avg Transitions',
                field: 'avgTransitions',
                type: 'numericColumn',
                headerClass: 'num-header',
                cellClass: 'num-cell',
                minWidth: 120,
                flex: 0.85,
                filter: 'agNumberColumnFilter',
                valueFormatter: p => Number(p.value || 0).toFixed(1),
                headerTooltip: metricTooltip('avgTransitions'),
                tooltipValueGetter: p => `${Number(p.value || 0).toFixed(1)} average transitions

${metricTooltip('avgTransitions')}`,
                menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
              },
              {
                headerName: 'Rework Signal',
                field: 'reworkRate',
                type: 'numericColumn',
                headerClass: 'num-header',
                cellClass: 'num-cell',
                minWidth: 120,
                flex: 0.85,
                filter: 'agNumberColumnFilter',
                valueFormatter: p => pct(p.value),
                headerTooltip: metricTooltip('reworkRate'),
                tooltipValueGetter: p => `${pct(p.value)}

${metricTooltip('reworkRate')}`,
                menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
                cellRenderer: badgeCellRenderer(v => pct(v), v => tierForRate(v, { watch: 0.15, high: 0.25 })),
              },
              {
                headerName: 'Wait Time',
                field: 'avgWaitTime',
                type: 'numericColumn',
                headerClass: 'num-header',
                cellClass: 'num-cell',
                minWidth: 105,
                flex: 0.75,
                filter: 'agNumberColumnFilter',
                valueFormatter: p => p.value ? formatDuration(p.value) : '—',
                headerTooltip: metricTooltip('avgWaitTime'),
                tooltipValueGetter: p => `${p.value ? formatDuration(p.value) : 'No wait-time data'}

${metricTooltip('avgWaitTime')}`,
                menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
              },
              {
                headerName: 'Flow Efficiency',
                field: 'avgFlowEfficiency',
                type: 'numericColumn',
                headerClass: 'num-header',
                cellClass: 'num-cell',
                minWidth: 120,
                flex: 0.85,
                filter: 'agNumberColumnFilter',
                valueFormatter: p => p.value == null ? '—' : pct(p.value),
                headerTooltip: metricTooltip('avgFlowEfficiency'),
                tooltipValueGetter: p => `${p.value == null ? 'No flow-efficiency data' : pct(p.value)}

${metricTooltip('avgFlowEfficiency')}`,
                menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
                cellRenderer: badgeCellRenderer(
                  v => pct(v),
                  v => tierForRate(v, { watch: 0.15, high: 0.3, invert: true })
                ),
              },
              ...(!isFreeEdition() ? [{
                headerName: 'Finding',
                field: 'finding',
                minWidth: 80,
                flex: 0.5,
                sortable: false,
                filter: false,
                headerClass: 'center-header',
                cellClass: 'center-cell',
                headerTooltip: metricTooltip('finding'),
                tooltipValueGetter: () => metricTooltip('finding'),
                cellRenderer: p => (
                  <button style={styles.saveBtn} title="Save segment finding" onClick={() => saveSegmentFinding(p.data)}>
                    <Lightbulb size={15} />
                  </button>
                ),
              }] : []),
            ]}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              floatingFilter: false,
              menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
            }}
            enableBrowserTooltips={true}
            tooltipShowDelay={250}
            animateRows={true}
            domLayout="autoHeight"
            rowHeight={40}
            headerHeight={48}
            suppressCellFocus={true}
            enableCellTextSelection={true}
          />
        </div>
      )}

      <InsightStrip
        style={{ marginTop: 12 }}
        items={[
          (highestRework && highestRework.reworkRate > 0) ? {
            label: 'Highest rework signal',
            value: `${highestRework.segment} at ${pct(highestRework.reworkRate)} — candidate for Explorer investigation`,
          } : null,
        ]}
      />

      <EvidenceFooterNote icon={<Lightbulb size={14} />}>
        Segment Evidence is derived from task and variant aggregates grouped by the selected context field. Use rework and flow-efficiency signals to prioritise where to investigate further.
      </EvidenceFooterNote>
    </div>
  );
}
